import React, { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { useEventListener, useMediaQuery, useTimeout } from "usehooks-ts";
import { TooltipVisibilityContext, useDragToDismiss } from "@picklebet/ui";
import { Betslip } from "@/features/betslip/components/Betslip";
import { PickSlip } from "@/features/pickems/components/pick-slip/PickSlip";
import {
  type DockContent,
  dockedQuery,
  railQuery,
  useShellStore,
} from "../stores/shellStore";
import { AppShell } from "./AppShell";
import { ClientOnly } from "./ClientOnly";

import * as styles from "./ShellDock.module.scss";

type ShellDockProps = {
  content: DockContent;
  isOpen: boolean;
};

// Never unmounts, so a half-built slip survives closing and a resize.
export const ShellDock = ({ content, isOpen }: ShellDockProps) => {
  const closeDock = useShellStore((state) => state.closeDock);
  const isDocked = useMediaQuery(dockedQuery);
  const hasCoarsePointer = useMediaQuery("(pointer: coarse)");
  const hasRail = useMediaQuery(railQuery);
  const cardRef = useRef<HTMLDivElement>(null);

  const isShowing = isOpen || isDocked;

  const { offset, dragProps } = useDragToDismiss({
    edge: "right",
    enabled: !isDocked && (hasCoarsePointer || !hasRail),
    onDismiss: closeDock,
    isElastic: false,
    isDirectionLocked: true,
  });

  // Held through the slide-out so the slip doesn't empty mid-animation.
  const [isVisible, setIsVisible] = useState(isShowing);
  if (isShowing && !isVisible) {
    setIsVisible(true);
  }
  useTimeout(() => setIsVisible(false), isShowing ? null : 300);

  // Runs inside the store write, before React commits, so a stake input
  // loses focus as the close starts.
  useEffect(
    () =>
      useShellStore.subscribe((state, previous) => {
        if (
          state.dockOpen ||
          !previous.dockOpen ||
          window.matchMedia(dockedQuery).matches
        ) {
          return;
        }

        const focused = document.activeElement;
        if (
          focused instanceof HTMLElement &&
          cardRef.current?.contains(focused)
        ) {
          focused.blur();
        }
      }),
    [],
  );

  useEffect(() => {
    if (isShowing) offset.jump(0);
  }, [isShowing, offset]);

  useEventListener("keydown", (event) => {
    if (
      isOpen &&
      !isDocked &&
      event.key === "Escape" &&
      !event.defaultPrevented
    ) {
      closeDock();
    }
  });

  return (
    <>
      <div className={styles.backdrop} onClick={closeDock} aria-hidden />
      <AppShell.Dock>
        <motion.div ref={cardRef} className={styles.card} {...dragProps}>
          <TooltipVisibilityContext.Provider value={isShowing}>
            <ClientOnly>
              {content === "betslip" ? (
                <Betslip isVisible={isVisible} />
              ) : (
                <PickSlip />
              )}
            </ClientOnly>
          </TooltipVisibilityContext.Provider>
        </motion.div>
      </AppShell.Dock>
    </>
  );
};
